/**
 * Создает throttled-версию функции, которая выполняется не чаще,
 * чем один раз в указанный период времени.
 * 
 * Сгенерено ИИ
 * 
 * @param {Function} func - Целевая функция для ограничения.
 * @param {number} wait - Количество миллисекунд для троттлинга.
 * @param {Object} [options={}] - Опции.
 * @param {boolean} [options.leading=true] - Указывает, следует ли выполнять функцию
 *                                           в начале интервала.
 * @param {boolean} [options.trailing=true] - Указывает, следует ли выполнять функцию
 *                                            в конце интервала.
 * @returns {Function} Новая throttled-функция.
 */
export function throttle(func, wait, options = {}) {
  // Значения по умолчанию для опций
  const { leading = true, trailing = true } = options;

  let timeoutId = null;
  let lastArgs = null;
  let lastThis = null;
  let lastCallTime = 0;
  let result;

  // Функция, которая будет выполняться по таймеру (trailing edge)
  const later = () => {
    // Сбрасываем время последнего вызова, если leading=false
    lastCallTime = !leading ? 0 : Date.now();
    timeoutId = null;

    // Если есть сохраненные аргументы, вызываем функцию
    if (lastArgs) {
      result = func.apply(lastThis, lastArgs);
      lastArgs = null;
      lastThis = null;
    }
  };

  // Основная функция, которую будем возвращать
  const throttled = function (...args) {
    const now = Date.now();

    // Если leading=false и это первый вызов, сбрасываем lastCallTime
    if (!lastCallTime && !leading) {
      lastCallTime = now;
    }

    // Вычисляем оставшееся время до следующего возможного вызова
    const remaining = wait - (now - lastCallTime);

    // Сохраняем контекст и аргументы для возможного trailing вызова
    lastArgs = args;
    lastThis = this;

    // Если время вышло или мы его "перескочили" (например, при смене вкладки)
    if (remaining <= 0 || remaining > wait) {
      // Если уже есть таймаут, очищаем его
      if (timeoutId) {
        clearTimeout(timeoutId);
        timeoutId = null;
      }

      // Фиксируем время вызова и выполняем функцию
      lastCallTime = now;
      result = func.apply(this, args);
    }
    // Если trailing=true и нет активного таймаута
    else if (!timeoutId && trailing) {
      // Устанавливаем таймаут на оставшееся время
      timeoutId = setTimeout(later, remaining);
    }

    // Возвращаем результат последнего вызова
    return result;
  };

  // Метод для отмены запланированного вызова
  throttled.cancel = function () {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    // Сбрасываем все внутренние состояния
    lastCallTime = 0;
    timeoutId = null;
    lastArgs = null;
    lastThis = null;
  };

  // Метод для немедленного выполнения, если есть отложенный вызов
  throttled.flush = function () {
    if (timeoutId) {
      clearTimeout(timeoutId);
      later();
    }
    return result;
  };

  return throttled;
}
